import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../../../lib/clients/supabase';
import { toast } from 'sonner';
import './GuestUsers.css';

const GuestUsers = ({ searchTerm, initialPage = 1, onPageChange }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when search term changes
  }, [searchTerm]);

  useEffect(() => {
    // Update current page when initialPage prop changes
    setCurrentPage(initialPage);
  }, [initialPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('guest_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
      setTotalPages(Math.ceil((data || []).length / itemsPerPage));
    } catch (error) {
      console.error('Error fetching guest users:', error);
      toast.error('Failed to fetch guest users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentItems = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    onPageChange?.(page); // Call the onPageChange prop if provided
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key="first"
          className="guest-users__pagination-btn"
          onClick={() => handlePageChange(1)}
        >
          «
        </button>
      );
    }

    // Previous page
    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          className="guest-users__pagination-btn"
          onClick={() => handlePageChange(currentPage - 1)}
        >
          ‹
        </button>
      );
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`guest-users__pagination-btn ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Next page
    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          className="guest-users__pagination-btn"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          ›
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      pages.push(
        <button
          key="last"
          className="guest-users__pagination-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          »
        </button>
      );
    }

    return pages;
  };

  const handleViewBookings = (userId) => {
    navigate(`/admin/bookings?userId=${userId}`);
  };

  if (loading) {
    return <div className="guest-users__loading">Loading guest users...</div>;
  }

  if (filteredUsers.length === 0) {
    return <div className="guest-users__empty">No guest users found</div>;
  }

  return (
    <div className="guest-users">
      <div className="guest-users__table-container">
        <div className="guest-users__table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map(user => (
                <tr key={user.id}>
                  <td>{`${user.first_name || ''} ${user.last_name || ''}`}</td>
                  <td className="guest-users__contact-cell">
                    <div className="contact-preview">
                      <span className="contact-icon">📧</span>
                      <span className="contact-label">Email</span>
                    </div>
                    <div className="contact-details">
                      <div className="contact-item">
                        <span className="contact-type">Email</span>
                        <span className="contact-value">{user.email || 'N/A'}</span>
                      </div>
                      <div className="contact-item">
                        <span className="contact-type">Phone</span>
                        <span className="contact-value">{user.phone || 'N/A'}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <button
                      className="guest-users__action-btn guest-users__action-btn--book"
                      onClick={() => handleViewBookings(user.id)}
                    >
                      View Bookings
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="guest-users__pagination">
        {renderPagination()}
      </div>
      
      <div className="guest-users__pagination-info">
        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
      </div>
    </div>
  );
};

export default GuestUsers; 